import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

// Peninsula milestones and distances (miles between consecutive points)
const MILESTONES = [
  { name: 'Cotton Tree', distance: 7 },
  { name: 'Lumley Beach', distance: 3 },
  { name: 'Goderich', distance: 11 },
  { name: 'No. 2 Beach', distance: 5 },
  { name: 'Kent', distance: 15 },
  { name: 'Waterloo', distance: 9 },
  { name: 'Jui', distance: 4 },
  { name: 'Calaba Town', distance: 3 },
  { name: 'Kissy', distance: 5 },
];

const TOTAL_MILES = MILESTONES.reduce((sum, m) => sum + m.distance, 0); // 62

interface MilesAchievedEntry {
  id: string;
  miles: number;
  dateAchieved: string;
  comments: string | null;
}

interface Props {
  participantName: string;
  participantId: string;
  milesSponsored: number;
  milesAchieved: number;
}

export function PeninsulaProgressLoop({ participantName, participantId, milesSponsored, milesAchieved: initialMilesAchieved }: Props) {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [viewType, setViewType] = useState<'circle' | 'bar'>('circle');
  const [milesEntries, setMilesEntries] = useState<MilesAchievedEntry[]>([]);
  const [totalAchieved, setTotalAchieved] = useState(initialMilesAchieved);
  const [showForm, setShowForm] = useState(false);
  const [formMiles, setFormMiles] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formComments, setFormComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (participantId) {
      fetchMilesAchieved();
    }
  }, [participantId]);

  async function fetchMilesAchieved() {
    try {
      const data = await apiClient<MilesAchievedEntry[]>(`/fundraising/miles-achieved/${participantId}`);
      setMilesEntries(data);
      const total = data.reduce((sum, e) => sum + Number(e.miles), 0);
      setTotalAchieved(total);
    } catch {
      // If no entries yet, keep initial value
    }
  }

  async function handleAddMiles(e: React.FormEvent) {
    e.preventDefault();
    if (!formMiles || !formDate) return;
    setSaving(true);
    setError('');
    try {
      await apiClient('/fundraising/miles-achieved', {
        method: 'POST',
        body: {
          participantId,
          miles: parseFloat(formMiles),
          dateAchieved: formDate,
          comments: formComments || undefined,
        },
      });
      setFormMiles('');
      setFormDate('');
      setFormComments('');
      setShowForm(false);
      await fetchMilesAchieved();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add miles.');
    } finally {
      setSaving(false);
    }
  }

  const cx = 200;
  const cy = 200;
  const outerRadius = 160;
  const innerRadius = 120;
  const trackWidth = 18;
  const labelRadius = outerRadius + 28;

  // Calculate cumulative angles for each milestone
  const milestoneAngles: number[] = [];
  let cumulative = 0;
  for (let i = 0; i < MILESTONES.length; i++) {
    milestoneAngles.push((cumulative / TOTAL_MILES) * 360);
    cumulative += MILESTONES[i].distance;
  }

  function polarToXY(angleDeg: number, radius: number): { x: number; y: number } {
    // Counter-clockwise: negate the angle
    const rad = ((-angleDeg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  }

  function arcPath(startAngle: number, endAngle: number, radius: number): string {
    if (endAngle - startAngle >= 360) {
      const p1 = polarToXY(0, radius);
      const p2 = polarToXY(180, radius);
      return `M ${p1.x} ${p1.y} A ${radius} ${radius} 0 1 0 ${p2.x} ${p2.y} A ${radius} ${radius} 0 1 0 ${p1.x} ${p1.y}`;
    }
    const start = polarToXY(startAngle, radius);
    const end = polarToXY(endAngle, radius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    // Counter-clockwise: sweep flag = 0
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`;
  }

  const sponsoredAngle = Math.min((milesSponsored / TOTAL_MILES) * 360, 360);
  const achievedAngle = Math.min((totalAchieved / TOTAL_MILES) * 360, 360);

  // Multi-loop: calculate how many complete loops and the remainder
  const sponsoredLoops = Math.floor(milesSponsored / TOTAL_MILES);
  const sponsoredRemainder = milesSponsored % TOTAL_MILES;
  const sponsoredRemainderAngle = (sponsoredRemainder / TOTAL_MILES) * 360;

  const achievedLoops = Math.floor(totalAchieved / TOTAL_MILES);
  const achievedRemainder = totalAchieved % TOTAL_MILES;
  const achievedRemainderAngle = (achievedRemainder / TOTAL_MILES) * 360;

  return (
    <div style={{ textAlign: 'center', padding: '1rem' }}>
      <h4 style={{ color: '#0f2d1f', margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>{participantName}</h4>

      {/* Toggle between circle and bar */}
      <div style={{ marginBottom: '0.75rem' }}>
        <button
          onClick={() => setViewType('circle')}
          style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #ccc', borderRadius: '4px 0 0 4px', cursor: 'pointer', backgroundColor: viewType === 'circle' ? '#0f2d1f' : '#fff', color: viewType === 'circle' ? '#fff' : '#333' }}
        >
          ⭕ Loop
        </button>
        <button
          onClick={() => setViewType('bar')}
          style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #ccc', borderLeft: 'none', borderRadius: '0 4px 4px 0', cursor: 'pointer', backgroundColor: viewType === 'bar' ? '#0f2d1f' : '#fff', color: viewType === 'bar' ? '#fff' : '#333' }}
        >
          📊 Bar
        </button>
      </div>

      {viewType === 'bar' ? (
        /* Horizontal Progress Bar View */
        <div style={{ textAlign: 'left', maxWidth: '700px', margin: '0 auto' }}>
          {/* Milestone track with colorful numbered circles */}
          <div style={{ position: 'relative', padding: '0 20px', marginBottom: '1.5rem' }}>
            {/* Connecting line */}
            <div style={{ position: 'absolute', top: '16px', left: '20px', right: '20px', height: '3px', backgroundColor: '#ddd', borderRadius: '2px' }} />

            {/* Milestone circles */}
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
              {MILESTONES.map((milestone, i) => {
                const colors = ['#e53935', '#fb8c00', '#fdd835', '#43a047', '#1e88e5', '#8e24aa', '#00acc1', '#6d4c41', '#546e7a'];
                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '60px' }}>
                    <div
                      style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        backgroundColor: colors[i], color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: 700, border: '3px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)', position: 'relative', zIndex: 1,
                      }}
                      title={`${milestone.name} (${milestone.distance} mi to next)`}
                    >
                      {i + 1}
                    </div>
                    <div style={{ fontSize: '0.55rem', fontWeight: 600, color: '#000', textAlign: 'center', marginTop: '4px', lineHeight: 1.2, maxWidth: '55px' }}>
                      {milestone.name}
                    </div>
                    <div style={{ fontSize: '0.5rem', color: '#666', marginTop: '1px' }}>
                      {milestone.distance}mi
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Achieved Progress Bar (green) */}
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a6b3c' }}>Miles Achieved</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a6b3c' }}>{totalAchieved.toLocaleString()} / {TOTAL_MILES} mi</span>
            </div>
            <div style={{ position: 'relative', height: '24px', backgroundColor: '#e8e8e8', borderRadius: '12px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%', width: `${Math.min((totalAchieved / TOTAL_MILES) * 100, 100)}%`,
                  backgroundColor: '#1a6b3c', borderRadius: '12px',
                  transition: 'width 0.5s ease', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px',
                }}
              >
                {totalAchieved > 0 && (
                  <span style={{ fontSize: '0.65rem', color: '#fff', fontWeight: 700 }}>
                    {Math.round((totalAchieved / TOTAL_MILES) * 100)}%
                  </span>
                )}
              </div>
              {/* Milestone tick marks on the bar */}
              {(() => {
                let cum = 0;
                return MILESTONES.map((m, i) => {
                  cum += m.distance;
                  const pct = (cum / TOTAL_MILES) * 100;
                  if (i === MILESTONES.length - 1) return null;
                  return <div key={i} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: '1px', backgroundColor: 'rgba(255,255,255,0.5)' }} />;
                });
              })()}
            </div>
          </div>

          {/* Sponsored Progress Bar (gold) */}
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#c9a84c' }}>Miles Sponsored{sponsoredLoops > 0 && ` (Loop ${sponsoredLoops + 1})`}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#c9a84c' }}>{milesSponsored.toLocaleString()} / {TOTAL_MILES * (sponsoredLoops + 1)} mi</span>
            </div>
            {/* Show completed loops as full bars */}
            {sponsoredLoops > 0 && (
              <div style={{ marginBottom: '0.25rem', fontSize: '0.65rem', color: '#c9a84c', fontWeight: 600 }}>
                ✓ {sponsoredLoops} complete loop{sponsoredLoops > 1 ? 's' : ''} ({sponsoredLoops * TOTAL_MILES} mi)
              </div>
            )}
            <div style={{ position: 'relative', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '12px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%', width: `${Math.min(((sponsoredLoops > 0 ? sponsoredRemainder : milesSponsored) / TOTAL_MILES) * 100, 100)}%`,
                  backgroundColor: '#c9a84c', borderRadius: '12px',
                  transition: 'width 0.5s ease', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px',
                }}
              >
                {milesSponsored > 0 && (
                  <span style={{ fontSize: '0.65rem', color: '#fff', fontWeight: 700 }}>
                    {sponsoredLoops > 0 ? `${Math.round((sponsoredRemainder / TOTAL_MILES) * 100)}%` : `${Math.round((milesSponsored / TOTAL_MILES) * 100)}%`}
                  </span>
                )}
              </div>
              {/* Milestone tick marks on the bar */}
              {(() => {
                let cum = 0;
                return MILESTONES.map((m, i) => {
                  cum += m.distance;
                  const pct = (cum / TOTAL_MILES) * 100;
                  if (i === MILESTONES.length - 1) return null;
                  return <div key={i} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: '1px', backgroundColor: 'rgba(255,255,255,0.5)' }} />;
                });
              })()}
            </div>
          </div>

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8rem', marginTop: '0.5rem', color: '#333', flexWrap: 'wrap' }}>
            <div>🏃 <strong>{totalAchieved.toLocaleString()}</strong> mi achieved ({achievedLoops > 0 ? `${achievedLoops + 1} loops, ` : ''}{Math.round((totalAchieved / TOTAL_MILES) * 100)}% total)</div>
            <div>🤝 <strong>{milesSponsored.toLocaleString()}</strong> mi sponsored ({sponsoredLoops > 0 ? `${sponsoredLoops + 1} loops, ` : ''}{Math.round((milesSponsored / TOTAL_MILES) * 100)}% total)</div>
          </div>
        </div>
      ) : (
      <>
      <div style={{ position: 'relative', display: 'inline-block', width: '400px', maxWidth: '100%' }}>
        <svg width="400" height="400" viewBox="0 0 400 400" style={{ maxWidth: '100%', height: 'auto', position: 'relative' }}>
        {/* Clip path for circular center image */}
        <defs>
          <clipPath id={`center-clip-${participantId}`}>
            <circle cx={cx} cy={cy} r={innerRadius - trackWidth / 2} />
          </clipPath>
        </defs>

        {/* Center image (fills the inside of the inner ring) */}
        <image
          href="/FreetownPeninsulaLoop.png?v=3"
          x={cx - (innerRadius - trackWidth / 2)}
          y={cy - (innerRadius - trackWidth / 2)}
          width={(innerRadius - trackWidth / 2) * 2}
          height={(innerRadius - trackWidth / 2) * 2}
          clipPath={`url(#center-clip-${participantId})`}
          preserveAspectRatio="xMidYMid slice"
        />

        {/* Background tracks */}
        <circle cx={cx} cy={cy} r={outerRadius} fill="none" stroke="#e8e8e8" strokeWidth={trackWidth} />
        <circle cx={cx} cy={cy} r={innerRadius} fill="none" stroke="#f0f0f0" strokeWidth={trackWidth} />

        {/* Segment dividers and milestone dots */}
        {MILESTONES.map((milestone, i) => {
          const angle = milestoneAngles[i];
          const outerPoint = polarToXY(angle, outerRadius + trackWidth / 2 + 2);
          const innerPoint = polarToXY(angle, innerRadius - trackWidth / 2 - 2);
          const dotPoint = polarToXY(angle, (outerRadius + innerRadius) / 2);
          const labelPoint = polarToXY(angle, labelRadius);
          const nextAngle = i < MILESTONES.length - 1 ? milestoneAngles[i + 1] : 360;

          return (
            <g key={i}>
              <line x1={innerPoint.x} y1={innerPoint.y} x2={outerPoint.x} y2={outerPoint.y} stroke="#ccc" strokeWidth={1} />
              <circle cx={dotPoint.x} cy={dotPoint.y} r={4} fill="#0f2d1f" />
              <text x={labelPoint.x} y={labelPoint.y} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#000" fontWeight={hoveredSegment === i ? 700 : 600}>
                {milestone.name}
              </text>
              <path
                d={arcPath(angle, nextAngle, (outerRadius + innerRadius) / 2)}
                fill="none" stroke="transparent" strokeWidth={outerRadius - innerRadius + trackWidth}
                onMouseEnter={() => setHoveredSegment(i)}
                onMouseLeave={() => setHoveredSegment(null)}
                style={{ cursor: 'pointer' }}
              />
            </g>
          );
        })}

        {/* Outer ring progress (miles achieved) - green */}
        {achievedLoops > 0 && (
          <path d={arcPath(0, 359.99, outerRadius)} fill="none" stroke="#1a6b3c" strokeWidth={trackWidth} strokeLinecap="round" opacity={0.4} />
        )}
        {achievedLoops > 1 && (
          <text x={cx} y={cy - outerRadius - 24} textAnchor="middle" fontSize="10" fill="#1a6b3c" fontWeight={700}>
            🏃 Loop {achievedLoops + 1}
          </text>
        )}
        {(achievedLoops > 0 ? achievedRemainderAngle : achievedAngle) > 0 && (
          <path d={arcPath(0, achievedLoops > 0 ? achievedRemainderAngle : achievedAngle, outerRadius)} fill="none" stroke="#1a6b3c" strokeWidth={trackWidth} strokeLinecap="round" />
        )}

        {/* Inner ring progress (miles sponsored) - gold */}
        {sponsoredLoops > 0 && (
          <path d={arcPath(0, 359.99, innerRadius)} fill="none" stroke="#c9a84c" strokeWidth={trackWidth} strokeLinecap="round" opacity={0.4} />
        )}
        {sponsoredLoops > 1 && (
          <text x={cx} y={cy + innerRadius + 32} textAnchor="middle" fontSize="10" fill="#c9a84c" fontWeight={700}>
            🤝 Loop {sponsoredLoops + 1}
          </text>
        )}
        {(sponsoredLoops > 0 ? sponsoredRemainderAngle : sponsoredAngle) > 0 && (
          <path d={arcPath(0, sponsoredLoops > 0 ? sponsoredRemainderAngle : sponsoredAngle, innerRadius)} fill="none" stroke="#c9a84c" strokeWidth={trackWidth} strokeLinecap="round" />
        )}

        {hoveredSegment !== null && (
          <>
            <rect x={cx - 90} y={cy + 40} width={180} height={22} rx={4} fill="#fff" stroke="#ccc" strokeWidth={1} />
            <text x={cx} y={cy + 54} textAnchor="middle" fontSize="10" fill="#000" fontWeight={700}>
              {MILESTONES[hoveredSegment].name} → {MILESTONES[(hoveredSegment + 1) % MILESTONES.length].name}: {MILESTONES[hoveredSegment].distance} mi
            </text>
          </>
        )}
      </svg>
      </div>

      {/* Stats below the loop */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '0.5rem', fontSize: '0.85rem', flexWrap: 'wrap' }}>
        <div><span style={{ color: '#1a6b3c', fontWeight: 700 }}>{totalAchieved.toLocaleString()} mi</span> achieved{achievedLoops > 0 && ` (${achievedLoops + 1} loops)`}</div>
        <div><span style={{ color: '#c9a84c', fontWeight: 700 }}>{milesSponsored.toLocaleString()} mi</span> sponsored{sponsoredLoops > 0 && ` (${sponsoredLoops + 1} loops)`}</div>
        <div style={{ color: '#666' }}>{TOTAL_MILES} mi per loop</div>
      </div>
      </>
      )}

      {/* Add Miles Achieved Section */}
      <div style={{ marginTop: '1rem', textAlign: 'left', maxWidth: '500px', margin: '1rem auto 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <strong style={{ fontSize: '0.85rem', color: '#0f2d1f' }}>Miles Achieved Log</strong>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{ padding: '0.3rem 0.6rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          >
            {showForm ? 'Cancel' : '+ Add Miles'}
          </button>
        </div>

        {error && <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{error}</div>}

        {showForm && (
          <form onSubmit={handleAddMiles} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#333', marginBottom: '0.2rem' }}>Miles</label>
              <input type="number" step="0.1" min="0.1" value={formMiles} onChange={(e) => setFormMiles(e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem', width: '70px' }} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#333', marginBottom: '0.2rem' }}>Date Achieved</label>
              <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem' }} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#333', marginBottom: '0.2rem' }}>Comments</label>
              <input type="text" value={formComments} onChange={(e) => setFormComments(e.target.value)} placeholder="Optional" style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem', width: '150px' }} />
            </div>
            <button type="submit" disabled={saving} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </form>
        )}

        {/* Miles Achieved History */}
        {milesEntries.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Miles</th>
                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #ddd', fontWeight: 600 }}>Comments</th>
              </tr>
            </thead>
            <tbody>
              {milesEntries.map((entry) => (
                <tr key={entry.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '0.4rem' }}>{new Date(entry.dateAchieved).toLocaleDateString()}</td>
                  <td style={{ padding: '0.4rem', fontWeight: 500 }}>{Number(entry.miles).toLocaleString()}</td>
                  <td style={{ padding: '0.4rem', color: '#666' }}>{entry.comments || '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #0f2d1f', fontWeight: 700 }}>
                <td style={{ padding: '0.4rem' }}>Total</td>
                <td style={{ padding: '0.4rem' }}>{totalAchieved.toLocaleString()}</td>
                <td style={{ padding: '0.4rem' }}></td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <p style={{ color: '#666', fontSize: '0.8rem' }}>No miles achieved entries yet.</p>
        )}
      </div>
    </div>
  );
}
